import { noOp } from 'common/Constants';
import { ClientEvents, ClientTrackableEvents } from 'common/events/ClientEvents';
import React, { useState } from 'react';
import { ArrowContainer, Popover, PopoverProps } from 'react-tiny-popover';
import { useDebounceEffect } from 'src/shared/hooks/useDebounceEffect';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Colors, ZIndex } from 'web-common/src/shared/styles';
import { ContentContainer, InfoIcon, TriggerContainer } from './styles';

export interface TrackedTooltipProps<T extends ClientEvents>
  extends Omit<PopoverProps, 'isOpen' | 'content' | 'children'> {
  content: React.ReactNode;
  isOpen?: boolean;
  /** If true (default), append an info circle icon after `children` */
  showInfoCircle?: boolean;
  event: T;
  eventProperty: ClientTrackableEvents[T];
  children: React.ReactNode;
  closeOnClickMobile?: boolean;
}

/**
 * Tracks when tooltip is opened (100ms debounce)
 */
export const TrackedTooltip = <T extends ClientEvents>({
  isOpen,
  content,
  children,
  showInfoCircle,
  containerStyle,
  event,
  eventProperty,
  closeOnClickMobile,
  ...props
}: TrackedTooltipProps<T>): React.ReactElement<TrackedTooltipProps<T>> => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { isSmallScreen } = useScreenContext();
  const tracker = useTracker();

  useDebounceEffect(
    100,
    () => {
      if (isTooltipOpen) {
        tracker.track(event, eventProperty);
      }
    },
    [isTooltipOpen]
  );

  return (
    <Popover
      reposition
      isOpen={isOpen ?? isTooltipOpen}
      positions={['top', 'bottom', 'left', 'right']}
      onClickOutside={isSmallScreen ? () => setIsTooltipOpen(false) : noOp}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor={Colors.BLACK}
          arrowSize={8}
          arrowStyle={{ opacity: 0.85 }}>
          <ContentContainer>{content}</ContentContainer>
        </ArrowContainer>
      )}
      containerStyle={{
        zIndex: `${ZIndex.ModalContent}`,
        ...containerStyle,
      }}
      {...props}>
      <TriggerContainer
        onMouseEnter={() => setIsTooltipOpen(true)}
        onMouseLeave={() => setIsTooltipOpen(false)}
        onClick={isSmallScreen ? () => setIsTooltipOpen(closeOnClickMobile ? !isTooltipOpen : true) : noOp}>
        {children}
        {showInfoCircle && <InfoIcon />}
      </TriggerContainer>
    </Popover>
  );
};
